import { useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { InView } from 'react-intersection-observer'
import { Picture } from 'src/components/ui/Image'

import type { PictoCardInteractionHandlers, PictoItem } from './Pictos'

type Props = {
  items: PictoItem[]
  titleSection?: string
} & PictoCardInteractionHandlers

type ColumnItem = {
  item: PictoItem
  index: number
  tall?: boolean
}

const DEFAULT_VISIBLE_ITEMS = 5

export function PictosMobile({
  items,
  titleSection,
  onCardClick,
  onCardInView,
}: Props) {
  const [visibleItemsCount, setVisibleItemsCount] = useState(
    DEFAULT_VISIBLE_ITEMS
  )

  const safeVisibleItemsCount = Math.min(visibleItemsCount, items.length)

  const itemsToShow = useMemo(() => {
    return items.slice(0, safeVisibleItemsCount)
  }, [items, safeVisibleItemsCount])

  const columns = useMemo(() => {
    const leftColumn: ColumnItem[] = []
    const rightColumn: ColumnItem[] = []

    itemsToShow.forEach((item, index) => {
      const positionInBlock = index % DEFAULT_VISIBLE_ITEMS

      if (positionInBlock <= 2) {
        leftColumn.push({ item, index })

        return
      }

      if (positionInBlock === 3 || positionInBlock === 4) {
        rightColumn.push({ item, index, tall: true })
      }
    })

    return { leftColumn, rightColumn }
  }, [itemsToShow])

  const hasMoreItems = safeVisibleItemsCount < items.length

  return (
    <section
      className={`px-[7px] ${hasMoreItems ? 'pb-12' : ''}`}
      aria-label={titleSection}
    >
      <h2 className="m-0 mb-[18px] font-inter text-[20px] font-semibold leading-[30px] tracking-[-0.5px] text-restructure-primary">
        {titleSection}
      </h2>

      <div className="relative">
        <div className="grid grid-cols-2 gap-x-[13px] items-start">
          <ul className="flex flex-col gap-[18px] m-0 p-0 list-none">
            {columns.leftColumn.map(({ item, index, tall }) => (
              <InView
                as="li"
                threshold={0.6}
                onChange={(inView) => {
                  if (inView) {
                    onCardInView(item, index)
                  }
                }}
                triggerOnce
                key={`${item.text}-${index}`}
                data-testid="picto-card-mobile"
                className="w-full"
              >
                <Link
                  to={item.linkUrl}
                  className="flex flex-col gap-[10px] w-full text-inherit"
                  onClick={() => onCardClick(item, index)}
                  aria-label={`link para a categoria ${item.text}`}
                >
                  <span
                    className={`block w-full overflow-hidden bg-restructure-background-secondary ${
                      tall ? 'h-[282px]' : 'h-[176px]'
                    }`}
                  >
                    <Picture
                      className="block h-full w-full object-cover"
                      sources={[
                        {
                          src: item.image,
                          width: 183,
                          height: 282,
                          media: '(min-width: 767px)',
                        },
                      ]}
                      img={{
                        src: item.image,
                        width: 183,
                        height: 282,
                        'data-id': item.image,
                        title: item.text,
                        alt: item.altImage,
                        loading: 'eager',
                      }}
                      shouldCompress={false}
                    />
                  </span>

                  <h3 className="m-0 font-inter text-[12px] font-bold leading-[1.25] text-restructure-primary">
                    {item.text}
                  </h3>

                  {item.subtext ? (
                    <p className="m-0 font-inter text-[12px] font-normal leading-[16.5px] text-restructure-tertiary/85">
                      {item.subtext}
                    </p>
                  ) : null}
                </Link>
              </InView>
            ))}
          </ul>

          <ul className="flex flex-col gap-[18px] m-0 p-0 list-none">
            {columns.rightColumn.map(({ item, index, tall }) => (
              <InView
                as="li"
                threshold={0.6}
                onChange={(inView) => {
                  if (inView) {
                    onCardInView(item, index)
                  }
                }}
                triggerOnce
                key={`${item.text}-${index}`}
                data-testid="picto-card-mobile"
                className="w-full"
              >
                <Link
                  to={item.linkUrl}
                  className="flex flex-col gap-[10px] w-full text-inherit"
                  onClick={() => onCardClick(item, index)}
                  aria-label={`link para a categoria ${item.text}`}
                >
                  <span
                    className={`block w-full overflow-hidden bg-restructure-background-secondary ${
                      tall ? 'h-[282px]' : 'h-[176px]'
                    }`}
                  >
                    <Picture
                      className="block h-full w-full object-cover"
                      sources={[
                        {
                          src: item.image,
                          width: 183,
                          height: 282,
                          media: '(min-width: 767px)',
                        },
                      ]}
                      img={{
                        src: item.image,
                        width: 183,
                        height: 282,
                        'data-id': item.image,
                        title: item.text,
                        alt: item.altImage,
                        loading: 'eager',
                      }}
                      shouldCompress={false}
                    />
                  </span>

                  <h3 className="m-0 font-inter text-[12px] font-bold leading-[1.25] text-restructure-primary">
                    {item.text}
                  </h3>

                  {item.subtext ? (
                    <p className="m-0 font-inter text-[12px] font-normal leading-[16.5px] text-restructure-tertiary/85">
                      {item.subtext}
                    </p>
                  ) : null}
                </Link>
              </InView>
            ))}
          </ul>
        </div>

        {hasMoreItems ? (
          <div className="absolute -left-[7px] -right-[7px] -bottom-12 h-[153px] flex items-end justify-center px-5 pb-6 bg-gradient-to-t from-restructure-white via-restructure-white/95 via-[52%] to-restructure-white/0 backdrop-blur-[1px]">
            <button
              type="button"
              className="w-full min-h-[48px] rounded-full border border-restructure-border-primary bg-restructure-white text-body-sm font-medium text-restructure-primary"
              onClick={() => {
                setVisibleItemsCount((previousVisibleItemsCount) => {
                  return previousVisibleItemsCount + DEFAULT_VISIBLE_ITEMS
                })
              }}
              aria-label="Quero me inspirar mais"
            >
              Quero me inspirar mais
            </button>
          </div>
        ) : null}
      </div>
    </section>
  )
}
